import * as React from 'react'
import {ContentAreas} from '@freckle/student-entities/ts/common/helpers/content-area'
import {LoggedRoute} from '@freckle/student/ts/common/routers/logged-route'
import type {Chrome} from '@freckle/student/ts/pages/chromeless-launch/components/use-chrome'
import {
  ElaMenuHandler,
  ElaDiagnosticHandler,
  WordStudyIntroDebugHandler,
  WordStudyDebugHandlerComponent,
  ElaArticlesCategoriesHandler,
  ElaContentLibraryHandler,
  ElaArticlesCategoryHandler,
  ElaSkillPracticeDomainsHandler,
  ElaSkillPracticeDomainHandler,
  ElaSkillsPracticeSessionHandler,
  ElaTargetedSkillsPracticeSessionHandler,
  ElaSkillsPracticeDebugHandler,
  ElaWritingAssignmentHandler,
  ElaReadingAssignmentResumableContainer,
  ElaAssessmentSessionHandler,
  ElaSelfReadingAssignmentHandler,
  WordStudyBriefingHandler,
  SightWordsHandler,
  DecodablesBriefingHandler,
  DecodableHandlerSelf,
  DecodableHandlerTeacher,
  WordStudySessionHandler,
  WordStudySelfAssignedSessionHandler,
  ElaTargetedSelfAssignedHandler,
  WordStudyDiagnosticHandler,
  DomainPathwayHandler,
  FocusSkillsPracticeHandler,
  ElaFocusSkillsPracticeSessionHandler,
} from './ela-lazy'
import {
  NavigateToTeacherAssignedElaFocusSkills,
  NavigateToTeacherAssignedWordStudy,
  NavigateToTeacherAssignedElaSkillsPractice,
} from './legacy-route-redirects'
import type {PathData} from './path-data'

export type ElaRoutesWithQueryParams = {
  '/ela/menu': undefined
  '/ela/library': undefined
  '/ela/library/:elaLibraryContentType/categories': undefined
  '/ela/library/:elaLibraryContentType/categories/:category': undefined
  '/ela/word-study-debug/intros/:uspId': undefined
  '/ela/word-study-debug/questions/:uuid': undefined
  '/ela/questions/skill/:skillId': {
    chrome: Chrome
    numQuestions: number
  }
  '/ela/skills-practice/domains': undefined
  '/ela/skills-practice/domains/:domainId': undefined
  '/ela/skills-practice/domains/:domainId/session': undefined
  '/ela/skills-practice/self-assigned/assignments/:assignmentIds': {domainId: string}
  '/ela/skills-practice/teacher-assigned/assignments/:assignmentIds': undefined
  '/ela/skills-practice/assignments/:assignmentIds': undefined
  '/ela/system/skills-practice/assignments/:assignmentIds': undefined
  '/ela/system/targeted-skills-practice/assignments/:assignmentIds': undefined | {chrome: Chrome}
  '/ela/system/skills-practice/debug/:uuid?': undefined
  '/ela/assignments/writing/:assignmentIds': undefined
  '/ela/assignments/reading/:assignmentIds': undefined
  '/ela/assignments/assessment/:assignmentIds': undefined
  '/ela/assignments/self-assigned-reading/:articleUuid': undefined
  '/ela/word-study-briefing': undefined
  '/ela/sight-words': undefined
  '/ela/diagnostic': undefined
  '/ela/decodables': undefined
  '/ela/assignments/decodable/:assignmentIds': undefined
  '/ela/decodables/:decodableUuid': undefined

  // Legacy Word Study Route
  '/ela/assignments/word-study/:assignmentIds': undefined

  // Self assigned Word Study Assignment Creation Route
  '/ela/word-study-session': undefined

  '/ela/word-study/teacher-assigned/assignments/:assignmentIds': undefined
  '/ela/word-study/self-assigned/assignments/:assignmentIds': undefined | {chrome: Chrome}

  '/ela/word-study-diagnostic': undefined
  '/ela/focus-skills-practice': undefined
  // Legacy route to support teacher generated routes
  '/ela/focus-skills-practice/assignments/:assignmentIds': undefined

  '/ela/focus-skills-practice/self-assigned/assignments/:assignmentIds': undefined
  '/ela/focus-skills-practice/teacher-assigned/assignments/:assignmentIds': undefined
}

export const elaPathData: PathData<ElaRoutesWithQueryParams> = {
  '/ela/menu': {
    isBlockedByFocusMode: true,
    element: path => (
      <LoggedRoute component={ElaMenuHandler} wrapperParams={{isResponsive: true}} path={path} />
    ),
  },
  '/ela/library': {
    isBlockedByFocusMode: true,
    element: path => (
      <LoggedRoute
        component={ElaContentLibraryHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },
  '/ela/questions/skill/:skillId': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={ElaTargetedSelfAssignedHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },
  '/ela/library/:elaLibraryContentType/categories': {
    isBlockedByFocusMode: true,
    element: path => <LoggedRoute component={ElaArticlesCategoriesHandler} path={path} />,
  },
  '/ela/library/:elaLibraryContentType/categories/:category': {
    isBlockedByFocusMode: true,
    element: path => <LoggedRoute component={ElaArticlesCategoryHandler} path={path} />,
  },
  '/ela/word-study-debug/intros/:uspId': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={WordStudyIntroDebugHandler} path={path} />,
  },
  '/ela/word-study-debug/questions/:uuid': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={WordStudyDebugHandlerComponent} path={path} />,
  },
  '/ela/skills-practice/domains': {
    isBlockedByFocusMode: true,
    element: path => (
      <LoggedRoute
        component={ElaSkillPracticeDomainsHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },
  '/ela/skills-practice/domains/:domainId': {
    isBlockedByFocusMode: true,
    element: path => (
      <LoggedRoute
        component={DomainPathwayHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },
  '/ela/skills-practice/domains/:domainId/session': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={ElaSkillPracticeDomainHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },
  '/ela/skills-practice/self-assigned/assignments/:assignmentIds': {
    isBlockedByFocusMode: true,
    element: path => <LoggedRoute component={ElaSkillsPracticeSessionHandler} path={path} />,
  },
  '/ela/skills-practice/teacher-assigned/assignments/:assignmentIds': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={ElaSkillsPracticeSessionHandler} path={path} />,
  },
  // Legacy route to support teacher generated routes
  '/ela/skills-practice/assignments/:assignmentIds': {
    isBlockedByFocusMode: false,
    element: () => <NavigateToTeacherAssignedElaSkillsPractice />,
  },
  '/ela/system/skills-practice/assignments/:assignmentIds': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={ElaSkillsPracticeSessionHandler} path={path} />,
  },
  '/ela/system/targeted-skills-practice/assignments/:assignmentIds': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute component={ElaTargetedSkillsPracticeSessionHandler} path={path} />
    ),
  },
  '/ela/system/skills-practice/debug/:uuid?': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={ElaSkillsPracticeDebugHandler} path={path} />,
  },
  '/ela/assignments/writing/:assignmentIds': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={ElaWritingAssignmentHandler} path={path} />,
  },
  '/ela/assignments/reading/:assignmentIds': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={ElaReadingAssignmentResumableContainer} path={path} />,
  },
  '/ela/assignments/assessment/:assignmentIds': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={ElaAssessmentSessionHandler} path={path} />,
  },
  '/ela/assignments/self-assigned-reading/:articleUuid': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={ElaSelfReadingAssignmentHandler} path={path} />,
  },
  '/ela/word-study-briefing': {
    isBlockedByFocusMode: true,
    element: path => <LoggedRoute component={WordStudyBriefingHandler} path={path} />,
  },
  '/ela/sight-words': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={SightWordsHandler} path={path} />,
  },
  '/ela/diagnostic': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={ElaDiagnosticHandler} path={path} />,
  },
  '/ela/decodables': {
    isBlockedByFocusMode: true,
    element: path => <LoggedRoute component={DecodablesBriefingHandler} path={path} />,
  },
  '/ela/assignments/decodable/:assignmentIds': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={DecodableHandlerTeacher} path={path} />,
  },
  '/ela/decodables/:decodableUuid': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={DecodableHandlerSelf} path={path} />,
  },

  '/ela/word-study/teacher-assigned/assignments/:assignmentIds': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={WordStudySessionHandler} path={path} />,
  },

  '/ela/word-study/self-assigned/assignments/:assignmentIds': {
    isBlockedByFocusMode: true,
    element: path => <LoggedRoute component={WordStudySessionHandler} path={path} />,
  },

  // Legacy word study route
  '/ela/assignments/word-study/:assignmentIds': {
    isBlockedByFocusMode: false,
    element: () => <NavigateToTeacherAssignedWordStudy />,
  },

  // self assigned word-study assignment creation route
  '/ela/word-study-session': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={WordStudySelfAssignedSessionHandler} path={path} />,
  },
  '/ela/word-study-diagnostic': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={WordStudyDiagnosticHandler} path={path} />,
  },
  '/ela/focus-skills-practice': {
    isBlockedByFocusMode: true,
    element: path => (
      <LoggedRoute
        component={() => <FocusSkillsPracticeHandler contentArea={ContentAreas.ela} />}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },
  '/ela/focus-skills-practice/teacher-assigned/assignments/:assignmentIds': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={ElaFocusSkillsPracticeSessionHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },
  '/ela/focus-skills-practice/self-assigned/assignments/:assignmentIds': {
    isBlockedByFocusMode: true,
    element: path => (
      <LoggedRoute
        component={ElaFocusSkillsPracticeSessionHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },
  // Legacy route to support teacher generated routes
  '/ela/focus-skills-practice/assignments/:assignmentIds': {
    isBlockedByFocusMode: false,
    element: () => <NavigateToTeacherAssignedElaFocusSkills />,
  },
}
